import { inject, Injectable } from "@angular/core";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { BaseResponse } from "@bitwarden/common/models/response/base.response";
import { ListResponse } from "@bitwarden/common/models/response/list.response";
import { OrganizationUserService } from "@vault/common/abstractions/organization-user/organization-user.service";
import { OrganizationUserAcceptRequest } from "@vault/common/abstractions/organization-user/requests";

// "Organization": first team in an org the user isn't a member of yet (users_organizations row,
// still Invited). "Collection": one more team in an org the user already belongs to
// (collection_invitations row). Each kind has its own accept/decline endpoints.
export type TeamInvitationType = "Organization" | "Collection";

// Invited: waiting on the invitee. Accepted (org invites only): the invitee accepted and is
// waiting for an admin to confirm them. Rejected (org invites only): an admin rejected them
// instead of confirming; shown until the invitee has seen it.
export type TeamInvitationStatus = "Invited" | "Accepted" | "Rejected";

export class TeamInvitationResponse extends BaseResponse {
  type: TeamInvitationType;
  status: TeamInvitationStatus;
  organizationId: string;
  organizationName: string;
  organizationUserId: string | null;
  collectionId: string | null;
  collectionName: string | null;
  // Organization invites only: plaintext name of the team(s) the invite is for, as sent by the
  // inviter. Null for invites that didn't carry one.
  teamName: string | null;
  invitedByEmail: string | null;
  // Null for org invites sent before gloss-back started recording users_organizations.invited_at.
  invitedAt: Date | null;
  acceptedAt: Date | null;
  rejectionId: string | null;
  rejectedAt: Date | null;
  token: string;

  constructor(response: any) {
    super(response);
    this.type = this.getResponseProperty("Type");
    this.status = this.getResponseProperty("Status") ?? "Invited";
    this.organizationId = this.getResponseProperty("OrganizationId");
    this.organizationName = this.getResponseProperty("OrganizationName");
    this.organizationUserId = this.getResponseProperty("OrganizationUserId") ?? null;
    this.collectionId = this.getResponseProperty("CollectionId") ?? null;
    this.collectionName = this.getResponseProperty("CollectionName") ?? null;
    this.teamName = this.getResponseProperty("TeamName") ?? null;
    this.invitedByEmail = this.getResponseProperty("InvitedByEmail") ?? null;
    const invitedAt = this.getResponseProperty("InvitedAt");
    this.invitedAt = invitedAt ? new Date(invitedAt) : null;
    const acceptedAt = this.getResponseProperty("AcceptedAt");
    this.acceptedAt = acceptedAt ? new Date(acceptedAt) : null;
    this.rejectionId = this.getResponseProperty("RejectionId") ?? null;
    const rejectedAt = this.getResponseProperty("RejectedAt");
    this.rejectedAt = rejectedAt ? new Date(rejectedAt) : null;
    this.token = this.getResponseProperty("Token");
  }
}

// A pending team invite as the team's Owner/Manager sees it: an existing org member invited to
// one more team who hasn't accepted yet. Until then they have no users_collections row, so they
// don't show up among the team's members any other way.
export class PendingTeamInviteResponse extends BaseResponse {
  userId: string;
  name: string;
  email: string;
  invitedByEmail: string | null;
  invitedAt: Date | null;

  constructor(response: any) {
    super(response);
    this.userId = this.getResponseProperty("UserId");
    this.name = this.getResponseProperty("Name") ?? "";
    this.email = this.getResponseProperty("Email");
    this.invitedByEmail = this.getResponseProperty("InvitedByEmail") ?? null;
    const invitedAt = this.getResponseProperty("InvitedAt");
    this.invitedAt = invitedAt ? new Date(invitedAt) : null;
  }
}

// A team invite the invitee declined, as the team's Owner/Manager sees it (with "Invite again").
export class DeclinedTeamInviteResponse extends BaseResponse {
  id: string;
  userId: string;
  name: string;
  email: string;
  declinedAt: Date | null;

  constructor(response: any) {
    super(response);
    this.id = this.getResponseProperty("Id");
    this.userId = this.getResponseProperty("UserId");
    this.name = this.getResponseProperty("Name") ?? "";
    this.email = this.getResponseProperty("Email");
    const declinedAt = this.getResponseProperty("DeclinedAt");
    this.declinedAt = declinedAt ? new Date(declinedAt) : null;
  }
}

// Wraps gloss-back's team invitation endpoints: the invitee's GET /organizations/invitations
// with the accept/decline call matching each invitation's type (so the Invitations tab doesn't
// need to know which endpoint is which), and the team owner's view of a team's pending invites.
@Injectable({
  providedIn: "root",
})
export class TeamInvitationService {
  private readonly apiService = inject(ApiService);
  private readonly organizationUserService = inject(OrganizationUserService);

  async getInvitations(): Promise<TeamInvitationResponse[]> {
    const r = await this.apiService.send("GET", "/organizations/invitations", null, true, true);
    return new ListResponse(r, TeamInvitationResponse).data;
  }

  async accept(invitation: TeamInvitationResponse): Promise<void> {
    if (invitation.type === "Organization") {
      const request = new OrganizationUserAcceptRequest();
      request.token = invitation.token;
      await this.organizationUserService.postOrganizationUserAccept(
        invitation.organizationId,
        invitation.organizationUserId,
        request,
      );
      return;
    }

    await this.apiService.send(
      "POST",
      this.collectionInvitePath(invitation, "accept"),
      { token: invitation.token },
      true,
      false,
    );
  }

  async decline(invitation: TeamInvitationResponse): Promise<void> {
    if (invitation.type === "Organization") {
      // Not /leave, which deletes the membership whatever its status: an invite accepted (maybe
      // confirmed) since the list loaded was "declined" by removing the member from the org.
      // This deletes it only while it's still Invited, checked in the same statement, and
      // otherwise answers "Invitation not found" (gloss-back#98). Only the invite named, and
      // only if it's the signed-in account's own: someone else's emailed link can't decline it.
      await this.apiService.send(
        "POST",
        `/organizations/${invitation.organizationId}/invitations/decline`,
        { organizationUserId: invitation.organizationUserId },
        true,
        false,
      );
      return;
    }

    await this.apiService.send(
      "POST",
      this.collectionInvitePath(invitation, "decline"),
      { token: invitation.token },
      true,
      false,
    );
  }

  // The invitee has been shown this rejection; it won't be listed again.
  async markRejectionSeen(rejectionId: string): Promise<void> {
    await this.apiService.send(
      "POST",
      `/organizations/invitations/rejections/${rejectionId}/seen`,
      null,
      true,
      false,
    );
  }

  async getTeamPendingInvites(
    organizationId: string,
    collectionId: string,
  ): Promise<PendingTeamInviteResponse[]> {
    const r = await this.apiService.send(
      "GET",
      `/organizations/${organizationId}/collections/${collectionId}/invitations`,
      null,
      true,
      true,
    );
    return new ListResponse(r, PendingTeamInviteResponse).data;
  }

  async cancelTeamInvite(
    organizationId: string,
    collectionId: string,
    userId: string,
  ): Promise<void> {
    await this.apiService.send(
      "POST",
      `/organizations/${organizationId}/collections/${collectionId}/invitations/${userId}/cancel`,
      null,
      true,
      false,
    );
  }

  // Re-sends a pending team invite's email. The new link replaces the old one, which stops
  // working (the backend only accepts the newest). Fails if mail isn't configured.
  async resendTeamInvite(
    organizationId: string,
    collectionId: string,
    userId: string,
  ): Promise<void> {
    await this.apiService.send(
      "POST",
      `/organizations/${organizationId}/collections/${collectionId}/invitations/${userId}/reinvite`,
      null,
      true,
      false,
    );
  }

  // How many confirmed members each team the caller can see has (collection id -> count). Open
  // to any confirmed member of the org, unlike the full member list.
  async getTeamMemberCounts(organizationId: string): Promise<Map<string, number>> {
    const r = await this.apiService.send(
      "GET",
      `/organizations/${organizationId}/team-member-counts`,
      null,
      true,
      true,
    );
    const counts = r?.Counts ?? r?.counts ?? {};
    return new Map(Object.entries(counts).map(([id, n]) => [id, Number(n)]));
  }

  async getTeamDeclines(
    organizationId: string,
    collectionId: string,
  ): Promise<DeclinedTeamInviteResponse[]> {
    const r = await this.apiService.send(
      "GET",
      `/organizations/${organizationId}/collections/${collectionId}/declines`,
      null,
      true,
      true,
    );
    return new ListResponse(r, DeclinedTeamInviteResponse).data;
  }

  // Dismisses a declined invite from the team's member list (inviting again clears it anyway).
  async removeTeamDecline(
    organizationId: string,
    collectionId: string,
    declineId: string,
  ): Promise<void> {
    await this.apiService.send(
      "POST",
      `/organizations/${organizationId}/collections/${collectionId}/declines/${declineId}/remove`,
      null,
      true,
      false,
    );
  }

  // Takes a confirmed member out of one team (and out of the org if it was their last) and emails
  // them about it, by the team's plaintext name (the server only has it encrypted).
  async removeTeamMember(
    organizationId: string,
    collectionId: string,
    organizationUserId: string,
    teamName: string,
  ): Promise<void> {
    await this.apiService.send(
      "POST",
      `/organizations/${organizationId}/collections/${collectionId}/members/${organizationUserId}/remove`,
      { teamName },
      true,
      false,
    );
  }

  // Deletes a team, sending its plaintext name along (the server only has it encrypted) so the
  // members who lose it can be emailed about it by name. Same endpoint as a plain collection
  // delete, in its POST form, which takes a body.
  async deleteTeam(organizationId: string, collectionId: string, teamName: string): Promise<void> {
    await this.apiService.send(
      "POST",
      `/organizations/${organizationId}/collections/${collectionId}/delete`,
      { id: collectionId, orgId: organizationId, teamName },
      true,
      false,
    );
  }

  private collectionInvitePath(invitation: TeamInvitationResponse, action: "accept" | "decline") {
    return `/organizations/${invitation.organizationId}/collections/${invitation.collectionId}/${action}`;
  }
}
